import { Modal } from 'antd';
import { useBranch } from '../../../../hooks/useBranch';
import { Check, Input, InputFile, Select } from '../../../../components';
import { useEffect, useMemo, useState, type FC, type PropsWithChildren } from 'react';
import { type IContent } from '../../../../types/content';
import { useGetSubjectsByBranch } from '../../../../services/subject';
import { useGetTopicsBySubject } from '../../../../services/topic';
import { useCreateContent, useUpdateContent } from '../../../../services/content';
import { parseSelect } from '../../../../utils/parseSelect';
import { getIframeLink } from '../../utils';
import clsx from 'clsx';
import { useTheme } from '../../../../contexts/ThemeContext';
import { IconFilePlus } from '@tabler/icons-react';

type Props = PropsWithChildren<{
    folders: string[]
    selectedSubject?: IContent
    isOpen: boolean
    title: string
    onCancel: () => void
    onFinish: () => Promise<void>
}>;

const ContentModal: FC<Props> = ({ folders, selectedSubject, isOpen, onCancel, title, onFinish }) => {
    const { branch } = useBranch();
    const { theme } = useTheme();

    const [type, setType] = useState('link');
    const [subject, setSubject] = useState('');
    const [topic, setTopic] = useState('');
    const [name, setName] = useState('');
    const [url, setUrl] = useState('');
    const [folder, setFolder] = useState('');
    const [downloadable, setDownloadable] = useState(false);
    const [isLink, setIsLink] = useState<boolean>(false);
    const [isIframe, setIsIframe] = useState<boolean>(false);
    const [iframeCode, setIframeCode] = useState<string>('');

    const [newFolder, setNewFolder] = useState<boolean>(false);

    const { subjects, loading: loadingSubjects } = useGetSubjectsByBranch(branch?._id as string);
    const { topics, loading: loadingTopics } = useGetTopicsBySubject(subject as string);

    const parsedSubjects = useMemo(() => parseSelect({
        array: [...(subjects ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })),
        labelKey: 'name',
        valueKey: '_id'
    }), [subjects, selectedSubject]);

    const parsedTopics = useMemo(() => parseSelect({
        array: [...(topics ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })),
        labelKey: 'name',
        valueKey: '_id'
    }), [topics, selectedSubject]);

    const parsedFolders = useMemo(() => [
        { label: '---', value: '' },
        ...(folders ?? [])
            .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
            .map((folder) => ({
            label: folder,
            value: folder
            }))
    ], [folders]);

    useEffect(() => {
        if (selectedSubject) {
            setType(selectedSubject.type);
            setName(selectedSubject.name);
            setUrl(selectedSubject.url);
            setFolder(selectedSubject.folder ?? '');
            setIsIframe(!!selectedSubject.iframeCode);
            setIframeCode(selectedSubject.iframeCode ?? '');
            setDownloadable(selectedSubject.downloadable);
            setSubject(selectedSubject.subject as string);
            setTopic(selectedSubject.topic as string);
            setIsLink(!selectedSubject.url?.includes('crossfactory.s3.eu-south-2.amazonaws'));
        }
    }, [selectedSubject]);

    const { createContent, loading: creating } = useCreateContent();
    const { updateContent, loading: updating } = useUpdateContent();

    const resetForm = () => {
        setType('link');
        setName('');
        setUrl('');
        setDownloadable(false);
        setIsLink(false);
        setSubject('');
        setTopic('');
        setIframeCode('');
        setIsIframe(false);
    };

    const getResourceUrl = (url: string, type: string) => {
        setUrl(url);
        const isPdf = url.toLowerCase().includes('.pdf');
        setType(isPdf ? 'application/pdf' : (type || 'application/pdf'));
    };

    const onSave = async () => {
        const payload = {
            name,
            type,
            url: isIframe ? 'iframe' : url,
            branch: branch?._id || '',
            subject,
            topic,
            downloadable,
            folder,
            iframeCode: getIframeLink(iframeCode)
        };
        if (selectedSubject?._id) {
            await updateContent({ _id: selectedSubject?._id, ...payload });
        } else {
            await createContent(payload);
        }
        await onFinish();
        resetForm();
        onCancel();
    };

    const onCancelModal = () => {
        resetForm();
        onCancel();
    };

    return (
        <Modal
            title={
                <div className={clsx(
                    'flex items-center gap-3',
                    'text-gray-900 dark:text-white'
                )}>
                    <div className={clsx(
                        'w-10 h-10 rounded-lg flex items-center justify-center',
                        'bg-gradient-to-br from-blue-500 to-purple-500',
                        'dark:from-blue-600 dark:to-purple-600'
                    )}>
                        <IconFilePlus size={20} className="text-white" />
                    </div>
                    <span className="text-xl font-bold">{title}</span>
                </div>
            }
            open={isOpen}
            onOk={onSave}
            okText="Guardar"
            onCancel={onCancelModal}
            okButtonProps={{
                disabled: !name || !type || !branch || !subject || !topic,
                loading: creating || updating,
                className: clsx(
                    'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
                    'border-none shadow-md hover:shadow-lg',
                    'transition-all duration-300'
                )
            }}
            cancelButtonProps={{
                className: clsx(
                    'border-gray-300 dark:border-gray-600',
                    'text-gray-700 dark:text-gray-300',
                    'hover:border-gray-400 dark:hover:border-gray-500',
                    'transition-all duration-300'
                )
            }}
            width={600}
            className={clsx(
                'content-modal',
                theme === 'dark' ? 'dark-modal' : 'light-modal'
            )}
            style={{
                borderRadius: '16px',
                overflow: 'hidden'
            }}
            maskStyle={{
                backdropFilter: 'blur(4px)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }}
            wrapClassName={clsx(
                'content-modal-wrapper',
                theme === 'dark' ? 'dark-modal-wrapper' : 'light-modal-wrapper',
                'bottom-sheet-mobile'
            )}
            centered={false}
        >
            <div className={clsx(
                'flex flex-col space-y-4',
                'w-full'
            )}>
                {(!isLink && !isIframe) && (
                    <div className="flex flex-col space-y-2 w-full">
                        <InputFile value={url} type="any" name={'Cargar'} fallbackUrl={getResourceUrl} />
                    </div>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    <div className="flex-1">
            <Check checked={isLink} name='Enlace' onChange={({ target }) => setIsLink(target.checked)} />
                    </div>
                    <div className="flex-1">
            <Check disabled={isLink} checked={isIframe} name='Iframe' onChange={({ target }) => setIsIframe(target.checked)} />
                    </div>
                </div>

                <div className="flex flex-col space-y-2 w-full">
                    <Input
                        value={name}
                        placeholder='Nombre'
                        onChange={({ target }) => setName(target.value)}
                    />
                </div>

                {isIframe && (
                    <div className="flex flex-col space-y-2 w-full">
                        <Input
                            placeholder='Pega el código del Iframe'
                            onChange={({ target }) => setIframeCode(target.value)}
                            value={iframeCode}
                        />
                    </div>
                )}

                {(!isLink && !isIframe) && (
                    <div className="flex flex-col space-y-2 w-full">
                        <Check
                            checked={downloadable}
                            name='Descargable'
                            onChange={({ target }) => setDownloadable(target.checked)}
                        />
                    </div>
                )}

                {isLink && (
                    <div className="flex flex-col space-y-2 w-full">
                        <Input
                            value={url}
                            placeholder='Url'
                            onChange={({ target }) => setUrl(target.value)}
                        />
                    </div>
                )}

                <div className="flex flex-col space-y-2 w-full">
                    <Select
                        disabled={loadingSubjects}
                        loading={loadingSubjects}
                        value={subject}
                        options={parsedSubjects}
                        placeholder='Bloque'
                        onChange={(id) => setSubject(id)}
                    />
                </div>

                <div className="flex flex-col space-y-2 w-full">
                    <Select
                        disabled={!subject || loadingTopics}
                        loading={loadingTopics}
                        value={topic}
                        options={parsedTopics}
                        placeholder='Tema'
                        onChange={(id) => setTopic(id)}
                    />
                </div>

                <div className="flex flex-col space-y-2 w-full">
                    <Check
                        checked={newFolder}
                        name='Crear carpeta nueva'
                        onChange={({ target }) => setNewFolder(target.checked)}
                    />
                </div>

                {newFolder ? (
                    <div className="flex flex-col space-y-2 w-full">
                        <Input
                            placeholder='Carpeta'
                            onChange={(e) => setFolder(e.target.value)}
                            value={folder}
                        />
                    </div>
                ) : (
                    <div className="flex flex-col space-y-2 w-full">
                        <Select
                            placeholder="Carpeta"
                            options={parsedFolders}
                            onChange={setFolder}
                            value={folder}
                        />
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ContentModal;
